import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, ExternalLink, RefreshCw, Sparkles } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import TranslationBadge from "@/components/admin/TranslationBadge";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/autoseo — AutoSEO Command Center.
 *
 * Shows webhook health, bearer status, last imported article, import queue,
 * and recent import history. Logs button links out to the raw log source
 * (stubbed for now — user chooses observability tool later).
 */

const WEBHOOK_PATH = "/api/admin/journal/import/autoseo";

export default function AdminAutoSEO() {
  const { t } = useT();
  const [summary, setSummary] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dedupeReport, setDedupeReport] = useState(null);
  const [working, setWorking] = useState("");
  const [rollbackDate, setRollbackDate] = useState("");
  const [rollbackReport, setRollbackReport] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [cleanupAudit, setCleanupAudit] = useState(null);

  const runCleanupAudit = async () => {
    setWorking("cleanup-audit");
    try { setCleanupAudit(await adminApi.cleanupAudit()); }
    catch (e) { setErr(e.message); }
    finally { setWorking(""); }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [s, a, lg] = await Promise.all([adminApi.summary(), adminApi.listArticles(), adminApi.autoseoLog().catch(() => ({ log: [] }))]);
      setSummary(s);
      setArticles(a.articles || []);
      setAuditLog(lg.log || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePause = async () => {
    setWorking("pause");
    try { await adminApi.setAutoseoPaused(!summary?.autoseo?.paused); await load(); }
    catch (e) { setErr(e.message); }
    finally { setWorking(""); }
  };

  const runDedupe = async (dryRun) => {
    setWorking(dryRun ? "scan" : "dedupe");
    try { setDedupeReport(await adminApi.journalDedupe(dryRun)); if (!dryRun) await load(); }
    catch (e) { setErr(e.message); }
    finally { setWorking(""); }
  };

  const seo = summary?.autoseo || {};
  const imported = articles.filter((a) => a.source === "autoseo");
  const drafts = imported.filter((a) => a.status !== "published");
  const published = imported.filter((a) => a.status === "published");

  return (
    <AdminShell title={t("admin.autoseo.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">
        {t("admin.autoseo.intro")}
      </p>

      {/* Incident controls: pause imports + duplicate protection */}
      <section className="mb-10 border border-white/10 bg-white/[0.015] p-6" data-testid="autoseo-controls">
        <h2 className="font-serif text-xl text-bone mb-2">Import Controls and Duplicate Protection</h2>
        <p className="text-[13px] text-bone/60 max-w-2xl mb-5">
          Incoming AutoSEO deliveries are matched on source id, slug, canonical URL and normalized title,
          so redeliveries update the existing article instead of creating a copy. Articles you delete are
          tombstoned and will not be recreated. Use the controls below during an incident.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link data-testid="autoseo-import-status-link" to="/admin/autoseo/import-status"
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-gold/50 text-gold hover:bg-gold/10">
            Import Status →
          </Link>
          <button data-testid="autoseo-pause-toggle" onClick={togglePause} disabled={working === "pause"}
            className={`px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border ${seo.paused ? "border-emerald-400/60 text-emerald-300 hover:bg-emerald-400/10" : "border-amber-400/60 text-amber-300 hover:bg-amber-400/10"} disabled:opacity-40`}>
            {seo.paused ? "Resume AutoSEO imports" : "Pause AutoSEO imports"}
          </button>
          <span className={`text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 border ${seo.paused ? "border-amber-400/50 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`} data-testid="autoseo-pause-status">
            {seo.paused ? "Imports paused" : "Imports active"}
          </span>
          <span className="w-px h-6 bg-white/10 mx-1 hidden sm:block" aria-hidden />
          <button data-testid="autoseo-dedupe-scan" onClick={() => runDedupe(true)} disabled={!!working}
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-white/20 text-bone/70 hover:border-gold/50 disabled:opacity-40">
            Scan for duplicates
          </button>
          <button data-testid="autoseo-dedupe-run" onClick={() => runDedupe(false)} disabled={!!working}
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-red-400/50 text-red-300 hover:bg-red-400/10 disabled:opacity-40">
            Quarantine duplicates
          </button>
        </div>
        <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap items-center gap-3" data-testid="autoseo-rollback-row">
          <span className="text-[11px] tracking-[0.2em] uppercase text-bone/50">Batch rollback</span>
          <input type="date" value={rollbackDate} onChange={(e) => setRollbackDate(e.target.value)} data-testid="autoseo-rollback-date"
            className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2" />
          <button data-testid="autoseo-rollback-run" disabled={!rollbackDate || !!working}
            onClick={async () => {
              if (!window.confirm(`Quarantine every AutoSEO article created on or after ${rollbackDate}?`)) return;
              setWorking("rollback");
              try { const r = await adminApi.autoseoRollback(rollbackDate); setRollbackReport(r); await load(); }
              catch (e) { setErr(e.message); } finally { setWorking(""); }
            }}
            className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-red-400/50 text-red-300 hover:bg-red-400/10 disabled:opacity-40">
            Quarantine batch
          </button>
          {rollbackReport && (
            <span className="text-[13px] text-bone/70" data-testid="autoseo-rollback-report">
              {rollbackReport.quarantined} article(s) quarantined since {rollbackReport.since}.
            </span>
          )}
        </div>
        {dedupeReport && (
          <div className="mt-4 text-[13px] text-bone/80 space-y-1" data-testid="autoseo-dedupe-report">
            <p>{dedupeReport.dryRun ? "Scan result" : "Cleanup result"}: {dedupeReport.duplicateGroups} duplicate group(s), {dedupeReport.removed} record(s) {dedupeReport.dryRun ? "would be permanently removed" : "permanently removed (backup kept)"}.</p>
            {dedupeReport.removedSlugs?.length > 0 && (
              <p className="text-bone/50 font-mono text-[12px]">{dedupeReport.dryRun ? "Would quarantine" : "Quarantined"}: {dedupeReport.removedSlugs.join(", ")}</p>
            )}
            {dedupeReport.kept?.length > 0 && (
              <p className="text-emerald-300/80 font-mono text-[12px]">Kept canonical: {dedupeReport.kept.join(", ")}</p>
            )}
          </div>
        )}
        {auditLog.length > 0 && (
          <details className="mt-5 pt-4 border-t border-white/10" data-testid="autoseo-audit-log">
            <summary className="text-[11px] tracking-[0.2em] uppercase text-bone/50 cursor-pointer">Generation and publication audit log ({auditLog.length})</summary>
            <div className="mt-3 space-y-1.5">
              {auditLog.map((l) => (
                <p key={l.id} className="text-[12px] font-mono text-bone/60">
                  {String(l.at).slice(0, 19).replace("T", " ")} · <span className={l.action.startsWith("skipped") ? "text-amber-300" : "text-emerald-300/80"}>{l.action}</span>
                  {l.slug ? ` · ${l.slug}` : l.title ? ` · ${l.title}` : ""}{l.flags ? " · FLAGGED" : ""}{l.count !== undefined ? ` · ${l.count}` : ""}
                </p>
              ))}
            </div>
          </details>
        )}
      </section>

      {/* Mandatory cleanup protocol — one-time / repeatable read-only audit */}
      <section className="mb-10 border border-white/10 bg-white/[0.015] p-6" data-testid="autoseo-cleanup-audit-section">
        <h2 className="font-serif text-xl text-bone mb-2">AutoSEO Publication Check — Cleanup Audit</h2>
        <p className="text-[13px] text-bone/60 max-w-2xl mb-5">
          Read-only audit of every AutoSEO article against the mandatory cleanup protocol: external links,
          referral text, AI remnants and placeholders. Nothing is changed or deleted — open the article in the
          Journal editor and run the cleanup protocol there to fix it.
        </p>
        <button data-testid="autoseo-cleanup-audit-run" onClick={runCleanupAudit} disabled={!!working}
          className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-40">
          {working === "cleanup-audit" ? "Auditing…" : "Run cleanup audit"}
        </button>
        {cleanupAudit && (
          <div className="mt-4" data-testid="autoseo-cleanup-audit-result">
            <p className="text-[13px] text-bone/80 mb-3">
              Audited <strong>{cleanupAudit.audited}</strong> AutoSEO article(s) —{" "}
              <span className="text-emerald-300">{cleanupAudit.clean} clean</span>,{" "}
              <span className={cleanupAudit.dirty ? "text-red-300" : "text-emerald-300"}>{cleanupAudit.dirty} with remaining issues</span>.
            </p>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {(cleanupAudit.articles || []).filter((a) => !a.clean).map((a) => (
                <div key={a.id} className="border border-red-400/30 bg-red-400/[0.03] p-3" data-testid={`cleanup-audit-item-${a.slug}`}>
                  <p className="text-[12px] text-bone/85 font-mono">
                    {a.slug} · {a.status}{a.article_reference ? ` · ${a.article_reference}` : ""} · {a.failure_count} issue(s)
                    <Link to={`/admin/journal?edit=${a.slug}`} className="ml-2 text-gold hover:underline">open editor →</Link>
                  </p>
                  {Object.entries(a.failures_by_language || {}).map(([lg, fails]) => (
                    <ul key={lg} className="mt-1 text-[11px] text-bone/55 font-mono space-y-0.5">
                      {fails.slice(0, 5).map((f, i) => <li key={i}>[{lg}/{f.field} · {f.check}] “{f.passage}”</li>)}
                    </ul>
                  ))}
                </div>
              ))}
              {cleanupAudit.dirty === 0 && (
                <p className="text-[13px] text-emerald-300" data-testid="cleanup-audit-all-clean">All AutoSEO articles pass the cleanup protocol.</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Health */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="autoseo-health">
        <StatCard
          testId="autoseo-webhook-status"
          icon={seo.enabled ? CheckCircle2 : AlertCircle}
          label={t("admin.autoseo.webhookStatus")}
          value={seo.enabled ? t("admin.label.online") : t("admin.label.offline")}
          hint={seo.enabled ? t("admin.autoseo.accepting") : t("admin.autoseo.tokenMissing")}
          accent={seo.enabled ? "green" : "red"}
          loading={loading}
        />
        <StatCard
          testId="autoseo-token-status"
          icon={seo.enabled ? CheckCircle2 : AlertCircle}
          label={t("admin.autoseo.bearerStatus")}
          value={seo.enabled ? t("admin.label.configured") : t("admin.label.notSet")}
          hint="AUTOSEO_WEBHOOK_TOKEN"
          accent={seo.enabled ? "green" : "red"}
          loading={loading}
        />
        <StatCard
          testId="autoseo-waiting"
          icon={Sparkles}
          label={t("admin.autoseo.articlesWaiting")}
          value={drafts.length}
          hint={t("admin.dash.stat.drafts.hint")}
          loading={loading}
        />
        <StatCard
          testId="autoseo-imported"
          icon={CheckCircle2}
          label={t("admin.autoseo.totalImported")}
          value={imported.length}
          hint={`${published.length} ${t("admin.label.published").toLowerCase()}`}
          accent="green"
          loading={loading}
        />
      </section>

      {/* Config */}
      <section className="mb-10" data-testid="autoseo-config">
        <h2 className="font-serif text-xl text-bone mb-4">{t("admin.autoseo.connection")}</h2>
        <div className="border border-gold/15 bg-white/[0.02] p-5 md:p-6 grid gap-4">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-bone/50 mb-2">{t("admin.autoseo.endpoint")}</p>
            <code className="font-mono text-[13px] text-bone/85 break-all">{WEBHOOK_PATH}</code>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-bone/50 mb-2">{t("admin.autoseo.lastImport")}</p>
            {seo.last_import_slug ? (
              <div className="flex items-center gap-3">
                <Link
                  to={`/admin/journal?edit=${encodeURIComponent(seo.last_import_slug)}`}
                  className="text-[14px] text-gold hover:text-bone transition-colors truncate"
                >
                  {seo.last_import_title || seo.last_import_slug}
                </Link>
                <span className="text-[11px] text-bone/50">
                  {seo.last_import_at ? new Date(seo.last_import_at).toLocaleString() : ""}
                </span>
              </div>
            ) : (
              <p className="text-sm text-bone/50">{t("admin.autoseo.noImports")}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="autoseo-refresh"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold border border-gold/40 px-4 py-2 hover:bg-gold/10"
            >
              <RefreshCw size={12} strokeWidth={1.5} /> {t("admin.action.refresh")}
            </button>
            <a
              href="https://autoseo.dev"
              target="_blank"
              rel="noreferrer"
              data-testid="autoseo-open-logs"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/70 border border-white/15 px-4 py-2 hover:text-gold hover:border-gold/40"
            >
              {t("admin.autoseo.openLogs")} <ExternalLink size={11} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* Import history */}
      <section className="mb-10" data-testid="autoseo-history">
        <h2 className="font-serif text-xl text-bone mb-4">{t("admin.autoseo.history")}</h2>
        <div className="border border-gold/15 bg-white/[0.02]">
          {loading && <p className="p-5 text-sm text-bone/50">{t("admin.label.loading")}</p>}
          {!loading && imported.length === 0 && (
            <p className="p-5 text-sm text-bone/50">{t("admin.autoseo.historyEmpty")}</p>
          )}
          {imported.slice(0, 15).map((doc) => (
            <div key={doc.id} className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/5 last:border-b-0">
              <div className="min-w-0">
                <p className="text-[14px] text-bone truncate">
                  {(doc.title && (doc.title.en || doc.title.nl)) || doc.slug}
                </p>
                <div className="mt-0.5 flex items-center flex-wrap gap-1.5">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-bone/50">
                    {doc.category} · {doc.status} · {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : ""}
                  </span>
                  <TranslationBadge article={doc} />
                </div>
              </div>
              <Link
                to={`/admin/journal?edit=${doc.id}`}
                data-testid={`autoseo-open-${doc.slug}`}
                className="text-[10px] tracking-[0.28em] uppercase text-gold hover:text-bone flex-shrink-0"
              >
                {t("admin.action.open")} →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Future */}
      <section className="mb-4" data-testid="autoseo-future">
        <h2 className="font-serif text-xl text-bone mb-4">{t("admin.autoseo.scheduled")}</h2>
        <div className="border border-gold/15 bg-white/[0.02] p-6 text-center">
          <span className="inline-block text-[10px] tracking-[0.28em] uppercase text-gold/60 border border-gold/30 px-3 py-1.5">
            {t("admin.label.inDevelopment")}
          </span>
          <p className="mt-4 text-sm text-bone/50 max-w-md mx-auto">
            {t("admin.autoseo.scheduledHint")}
          </p>
        </div>
      </section>

      {err && <p className="mt-4 text-sm text-red-400" data-testid="autoseo-error">{err}</p>}
    </AdminShell>
  );
}
